import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MessageSquare, Brain, Lightbulb, Puzzle } from "lucide-react";

const services = [
  {
    icon: MessageSquare,
    title: "Terapia de Lenguaje",
    desc: "Intervención especializada en el desarrollo del habla, lenguaje comprensivo y expresivo, articulación y comunicación funcional.",
    color: "#1a6b7c",
  },
  {
    icon: Brain,
    title: "Terapia de Conducta",
    desc: "Estrategias basadas en evidencia para modificar conductas desafiantes y fortalecer habilidades adaptativas y sociales.",
    color: "#1e3a5f",
  },
  {
    icon: Lightbulb,
    title: "Terapia Cognitivo Conductual",
    desc: "Abordaje terapéutico que trabaja la relación entre pensamientos, emociones y comportamientos para mejorar el bienestar.",
    color: "#5a9a8a",
  },
  {
    icon: Puzzle,
    title: "Terapia Ocupacional",
    desc: "Desarrollo de habilidades de vida diaria, integración sensorial y motricidad para potenciar la independencia funcional.",
    color: "#1a6b7c",
  },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const cardAnim = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="terapias"
      ref={ref}
      className="py-24 bg-muted"
      aria-labelledby="services-heading"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-14"
        >
          <p className="font-sans text-xs font-semibold tracking-widest text-primary uppercase mb-3">
            Nuestras Terapias
          </p>
          <h2 id="services-heading" className="section-heading text-3xl sm:text-4xl mb-4">
            Servicios de intervención terapéutica
          </h2>
          <div className="w-12 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          role="list"
        >
          {services.map(({ icon: Icon, title, desc, color }) => (
            <motion.article
              key={title}
              variants={cardAnim}
              role="listitem"
              className="card-base p-7 group cursor-default"
            >
              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-200 group-hover:scale-105"
                style={{ background: `${color}12` }}
                aria-hidden="true"
              >
                <Icon size={22} style={{ color }} strokeWidth={1.75} />
              </div>

              <h3 className="font-serif font-bold text-[1.05rem] text-navy mb-3 leading-snug">
                {title}
              </h3>
              <p className="font-sans text-sm text-txtMuted leading-relaxed">
                {desc}
              </p>

              {/* Bottom accent */}
              <div
                className="mt-5 h-0.5 rounded-full w-0 group-hover:w-8 transition-all duration-300"
                style={{ background: color }}
                aria-hidden="true"
              />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
